/**
 * adxDiBlueBars.js
 * Frontend JavaScript implementation of adx_di_blue_bars.py indicator.
 * Computes ADX, ADXMA, +DI (PDI), -DI (MDI), Donchian Channel (upper, basis, lower),
 * and the blue_bar signal flag (ADX > ADXMA && +DI > -DI).
 */

export function calculateAdxDiBlueBars(
    candles,
    options = {}
) {
    const {
        diPeriod = 14,
        adxPeriod = 14,
        adxMaPeriod = 2,
        channelPeriod = 20,
        channelOffset = 0,
        showChannel = true,
    } = options;

    if (!Array.isArray(candles) || candles.length === 0) {
        return [];
    }

    const n = candles.length;
    const highs = candles.map(c => Number(c.high));
    const lows = candles.map(c => Number(c.low));
    const closes = candles.map(c => Number(c.close));

    // Allocate output arrays initialized to null/0
    const pdi = new Array(n).fill(null);
    const mdi = new Array(n).fill(null);
    const adx = new Array(n).fill(null);
    const adxma = new Array(n).fill(null);
    const upper = new Array(n).fill(null);
    const basis = new Array(n).fill(null);
    const lower = new Array(n).fill(null);
    const blueBar = new Array(n).fill(0);

    if (n < diPeriod) {
        return candles.map((c, i) => ({
            ...c,
            ADX: null,
            ADXMA: null,
            PDI: null,
            MDI: null,
            upper: null,
            basis: null,
            lower: null,
            blue_bar: 0,
        }));
    }

    // 1. Calculate True Range (TR) and Directional Movements (+DM, -DM)
    const tr = new Array(n).fill(0);
    const pdm = new Array(n).fill(0);
    const mdm = new Array(n).fill(0);

    tr[0] = highs[0] - lows[0];
    pdm[0] = 0;
    mdm[0] = 0;

    for (let i = 1; i < n; i++) {
        const upMove = highs[i] - highs[i - 1];
        const downMove = lows[i - 1] - lows[i];

        pdm[i] = (upMove > downMove && upMove > 0) ? upMove : 0;
        mdm[i] = (downMove > upMove && downMove > 0) ? downMove : 0;

        const c1 = highs[i] - lows[i];
        const c2 = Math.abs(highs[i] - closes[i - 1]);
        const c3 = Math.abs(lows[i] - closes[i - 1]);
        tr[i] = Math.max(c1, c2, c3);
    }

    // 2. Wilder RMA function
    const wilderRma = (values, period) => {
        const out = new Array(n).fill(null);
        if (n < period) return out;

        let sum = 0;
        for (let i = 0; i < period; i++) sum += values[i];
        let acc = sum / period;
        out[period - 1] = acc;

        for (let i = period; i < n; i++) {
            acc = (acc * (period - 1) + values[i]) / period;
            out[i] = acc;
        }
        return out;
    };

    const smoothedTr = wilderRma(tr, diPeriod);
    const smoothedPdm = wilderRma(pdm, diPeriod);
    const smoothedMdm = wilderRma(mdm, diPeriod);

    const dx = new Array(n).fill(null);

    for (let i = diPeriod - 1; i < n; i++) {
        const str = smoothedTr[i];
        if (str && str > 0) {
            const p = (100 * smoothedPdm[i]) / str;
            const m = (100 * smoothedMdm[i]) / str;
            pdi[i] = p;
            mdi[i] = m;

            const sum = p + m;
            if (sum > 0) {
                dx[i] = (100 * Math.abs(p - m)) / sum;
            }
        }
    }

    // 3. Smooth DX to get ADX
    if (adxPeriod === diPeriod) {
        let validCount = 0;
        let startIdx = -1;

        for (let i = 0; i < n; i++) {
            if (dx[i] !== null) {
                validCount++;
                if (validCount === adxPeriod) {
                    startIdx = i;
                    break;
                }
            }
        }

        if (startIdx !== -1) {
            let acc = 0;
            for (let i = startIdx - adxPeriod + 1; i <= startIdx; i++) acc += dx[i];
            acc /= adxPeriod;
            adx[startIdx] = acc;

            for (let i = startIdx + 1; i < n; i++) {
                if (dx[i] !== null) {
                    acc = (acc * (adxPeriod - 1) + dx[i]) / adxPeriod;
                    adx[i] = acc;
                }
            }
        }
    } else {
        const adxRma = wilderRma(dx.map(v => v || 0), adxPeriod);
        for (let i = 0; i < n; i++) adx[i] = adxRma[i];
    }

    // 4. Calculate ADXMA (SMA after ADX warmup)
    for (let i = 0; i < n; i++) {
        if (i >= adxMaPeriod - 1) {
            let sum = 0;
            let valid = true;
            for (let j = i - adxMaPeriod + 1; j <= i; j++) {
                if (adx[j] === null || isNaN(adx[j])) {
                    valid = false;
                    break;
                }
                sum += adx[j];
            }
            if (valid) {
                adxma[i] = sum / adxMaPeriod;
            }
        }
    }

    // 5. Compute Blue Bar condition: ADX > ADXMA && +DI > -DI
    for (let i = 0; i < n; i++) {
        if (
            adx[i] !== null &&
            adxma[i] !== null &&
            pdi[i] !== null &&
            mdi[i] !== null &&
            adx[i] > adxma[i] &&
            pdi[i] > mdi[i]
        ) {
            blueBar[i] = 1;
        } else {
            blueBar[i] = 0;
        }
    }

    // 6. Donchian Price Channel
    if (showChannel) {
        for (let i = channelPeriod - 1; i < n; i++) {
            const targetIdx = i - channelOffset;
            if (targetIdx >= channelPeriod - 1 && targetIdx < n) {
                let maxH = -Infinity;
                let minL = Infinity;
                for (let k = targetIdx - channelPeriod + 1; k <= targetIdx; k++) {
                    if (highs[k] > maxH) maxH = highs[k];
                    if (lows[k] < minL) minL = lows[k];
                }
                upper[i] = maxH;
                lower[i] = minL;
                basis[i] = (maxH + minL) / 2;
            }
        }
    }

    // Combine output into enriched candles
    return candles.map((c, i) => ({
        ...c,
        ADX: adx[i],
        ADXMA: adxma[i],
        PDI: pdi[i],
        MDI: mdi[i],
        upper: upper[i],
        basis: basis[i],
        lower: lower[i],
        blue_bar: blueBar[i],
    }));
}
